import { useEffect, useState } from 'react';

import OpenClassAPI from '../OpenClassAPI';
import {
  OpenClassBookingOrder,
  OpenClassBookingOrderDetails,
} from '../types/OpenClass';

export type OpenClassOrderDetails = {
  failedOrderIds: OpenClassBookingOrder['id'][];
  loading: boolean;
  ordersById: Map<OpenClassBookingOrder['id'], OpenClassBookingOrderDetails>;
};

// A class registration names only its order id, so the company and contact behind it cost
// one request each. Settled rather than raced, a hidden order shouldn't blank the rest.
export const useOpenClassOrderDetails = (
  orderIds: OpenClassBookingOrder['id'][],
): OpenClassOrderDetails => {
  const [ordersById, setOrdersById] = useState<
    Map<OpenClassBookingOrder['id'], OpenClassBookingOrderDetails>
  >(new Map());
  const [failedOrderIds, setFailedOrderIds] = useState<
    OpenClassBookingOrder['id'][]
  >([]);
  const [loading, setLoading] = useState(false);

  // Sorted and joined so the effect gets a stable dependency, same ids means no refetch
  const orderKey = Array.from(new Set(orderIds))
    .sort((left, right) => left - right)
    .join(',');

  useEffect(() => {
    const ids = orderKey ? orderKey.split(',').map(Number) : [];
    if (ids.length === 0) {
      setOrdersById(new Map());
      setFailedOrderIds([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    Promise.allSettled(
      ids.map((orderId) => OpenClassAPI.getBookingOrderDetails(orderId)),
    ).then((results) => {
      if (!active) return;

      const loaded = new Map<
        OpenClassBookingOrder['id'],
        OpenClassBookingOrderDetails
      >();
      const failed: OpenClassBookingOrder['id'][] = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled')
          loaded.set(ids[index], result.value.data);
        else failed.push(ids[index]);
      });

      setOrdersById(loaded);
      setFailedOrderIds(failed);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [orderKey]);

  return { failedOrderIds, loading, ordersById };
};
